import React, { useState } from "react";
import { useGetResultDetails } from "../../../hooks/resultsHooks/results.hooks";
import { getValue } from "../../../utils/object.utils";
import { KeyValueList } from "../../KeyValueList";
import { Card } from "../../card";
import { formatDateBasedOnTimezone } from "../../../utils/date.utils";
import { BtnSize, BtnType, Button } from "../../Button";
import { EditResultModal } from "../EditResultModal";
import {
  ALIGN_ITEMS,
  DISPLAY,
  FLEX_DIRECTION,
  JUSTIFY_CONTENT,
  SPACING,
} from "../../../Constants/Enums/styles.enum";
import { useDownloadMarkLeader } from "../../../hooks/resultsHooks/studentResult.hooks";
import { useDispatch } from "react-redux";
import { setErrorMessage } from "../../../Redux/Slices/alert.slice";
import { FontFamily } from "../../../Constants/Enums/FontFamily.enums";
import { PendingResultInfo } from "./PendingResultInfo";
import { DownloadLedger } from "./DownloadLedger";

interface Props {
  resultId: number;
}

// Icon Components for better visual UX
const EditIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ marginRight: "6px" }}
  >
    <path
      d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"
      fill="currentColor"
    />
  </svg>
);

export const ResultDetailsCard = ({ resultId }: Props) => {
  const dispatch = useDispatch();

  const [openEditModal, setOpenEditModal] = useState(false);

  const { isLoading: isResultDetailLoading, data: resultDetails } =
    useGetResultDetails({ resultId });

  const { isLoading: isDownloadingMarkLedger, mutate: downloadMarkLeadger } =
    useDownloadMarkLeader({
      onSuccess: (markLeagerData) => {
        const reportFile = getValue(markLeagerData, "reportFile", null);

        if (reportFile) {
          window.open(reportFile, "_blank");
        }
      },

      onError: () => {
        dispatch(setErrorMessage("Failed to download mark-leager"));
      },
    });

  if (isResultDetailLoading) return <div />;

  const label = getValue(resultDetails, "label", "");
  const branchName = getValue(resultDetails, "school_branch_name", "");
  const className = getValue(resultDetails, "class_name", "");
  const publishDate = getValue(resultDetails, "publish_date");
  const totalStudents = getValue(resultDetails, "total_students", "");
  const totalAttendance = getValue(resultDetails, "days_before_exam", "");

  const totalPendingResults = getValue(
    resultDetails,
    "peding_results_count",
    "",
  );

  const formattedPublishDate = publishDate
    ? formatDateBasedOnTimezone({
        date: publishDate,
      })
    : "N/A";

  const handleMarkLedgerDownload = () => {
    downloadMarkLeadger({ resultId });
  };

  return (
    <Card cardTitle={label}>
      <div
        style={{
          display: DISPLAY.FLEX,
          flexDirection: FLEX_DIRECTION.COLUMN,
          gap: SPACING.S12,
        }}
      >
        <DownloadLedger />

        <div
          style={{
            display: DISPLAY.FLEX,
            flexDirection: FLEX_DIRECTION.ROW,
            gap: SPACING.S12,
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              flex: "1 1 200px",
              backgroundColor: "#F8F9FA",
              padding: "10px 12px",
              borderRadius: "6px",
              border: `1px solid #E0E0E0`,
            }}
          >
            <div
              style={{
                fontFamily: FontFamily.ROBOTO_REGULAR,
                fontSize: "10px",
                color: "#666",
                marginBottom: "8px",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Basic Information
            </div>
            <KeyValueList
              lists={[
                { key: "Branch", value: branchName },
                { key: "Class", value: className },
                { key: "Total attendance", value: totalAttendance },
              ]}
            />
          </div>

          <div
            style={{
              flex: "1 1 200px",
              backgroundColor: "#F8F9FA",
              padding: "10px 12px",
              borderRadius: "6px",
              border: `1px solid #E0E0E0`,
            }}
          >
            <div
              style={{
                fontFamily: FontFamily.ROBOTO_REGULAR,
                fontSize: "10px",
                color: "#666",
                marginBottom: "8px",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Statistics
            </div>
            <KeyValueList
              lists={[
                { key: "Publish date", value: formattedPublishDate },
                { key: "Total students", value: totalStudents },
              ]}
            />

            <PendingResultInfo totalPendingResults={totalPendingResults} />
          </div>
        </div>

        <div
          style={{
            display: DISPLAY.FLEX,
            flexDirection: FLEX_DIRECTION.COLUMN,
            gap: SPACING.S12,
            paddingTop: SPACING.S8,
          }}
        >
          {/* Secondary Action Buttons */}
          <div
            style={{
              display: DISPLAY.FLEX,
              justifyContent: JUSTIFY_CONTENT.END,
              alignItems: ALIGN_ITEMS.CENTER,
              gap: SPACING.S10,
              paddingTop: SPACING.S4,
            }}
          >
            <Button
              text="Export mark ledger"
              btnType={BtnType.basic}
              btnSize={BtnSize.sm}
              onClick={handleMarkLedgerDownload}
              loading={isDownloadingMarkLedger}
            />
            <Button
              text="Edit Result"
              btnType={BtnType.primary}
              btnSize={BtnSize.sm}
              onClick={() => {
                setOpenEditModal(true);
              }}
              additionalComponent={<EditIcon />}
            />
          </div>
        </div>
      </div>

      <EditResultModal
        resultId={resultId}
        resultDetail={{
          publishDate,
          resultType: getValue(resultDetails, "result_type"),
          totalAttendance,
        }}
        onClose={() => {
          setOpenEditModal(false);
        }}
        open={openEditModal}
      />
    </Card>
  );
};
